import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, Ref } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import IconButton from 'components/partials/button/icon-button';

import { buildCalendarDays } from './helpers/build-calendar-days';
import { buildCalendarYears } from './helpers/build-calendar-years';

import Icons from 'assets/icons';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

type CalendarView = 'days' | 'years';

export interface DatePickerCalendarProps {
  viewDate: Dayjs;
  selectedDate: Dayjs | null;
  onSelectDate: (date: Dayjs) => void;
  onViewDateChange: (date: Dayjs) => void;
  /** Portaled to `document.body`, so its position is computed by the caller. */
  style?: CSSProperties;
  ref?: Ref<HTMLDivElement>;
  'data-testid'?: string;
}

const DatePickerCalendar = ({
  viewDate,
  selectedDate,
  onSelectDate,
  onViewDateChange,
  style,
  ref,
  'data-testid': dataTestId = 'date-picker-calendar',
}: DatePickerCalendarProps) => {
  const [view, setView] = useState<CalendarView>('days');
  const activeYearRef = useRef<HTMLButtonElement | null>(null);

  const calendarDays = buildCalendarDays(viewDate);
  const calendarYears = buildCalendarYears();
  const today = dayjs();

  useEffect(() => {
    if (view === 'years') {
      activeYearRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [view]);

  const handlePrevMonth = () => onViewDateChange(viewDate.subtract(1, 'month'));
  const handleNextMonth = () => onViewDateChange(viewDate.add(1, 'month'));

  const handleSelectYear = (year: number) => {
    onViewDateChange(viewDate.year(year));
    setView('days');
  };

  return (
    <div
      ref={ref}
      className="w-[280px] rounded-md border border-navy-200 bg-white p-3 shadow-lg"
      style={style}
      role="dialog"
      aria-label="Choose date"
      data-testid={dataTestId}
    >
      <div className="flex items-center justify-between pb-2">
        <div className="flex items-center gap-1.5">
          <span
            className="text-sm font-semibold text-navy-700"
            data-testid={`${dataTestId}-label`}
          >
            {viewDate.format('MMMM YYYY')}
          </span>
          <IconButton
            icon={
              <Icons.ChevronRight
                className={classNames(
                  'transition',
                  view === 'years' && 'rotate-90'
                )}
              />
            }
            variant="reserve-text"
            size="baby"
            aria-label={view === 'years' ? 'Show days' : 'Show years'}
            onClick={() => setView(view === 'days' ? 'years' : 'days')}
            data-testid={`${dataTestId}-toggle-view`}
          />
        </div>

        {view === 'days' && (
          <div className="flex items-center gap-1">
            <IconButton
              icon={<Icons.ChevronLeft />}
              variant="reserve-text"
              size="baby"
              aria-label="Previous month"
              onClick={handlePrevMonth}
              data-testid={`${dataTestId}-prev-month`}
            />
            <IconButton
              icon={<Icons.ChevronRight />}
              variant="reserve-text"
              size="baby"
              aria-label="Next month"
              onClick={handleNextMonth}
              data-testid={`${dataTestId}-next-month`}
            />
          </div>
        )}
      </div>

      {view === 'days' ? (
        <div data-testid={`${dataTestId}-days`}>
          <div className="grid grid-cols-7 text-center text-sm text-gray-700">
            {WEEKDAY_LABELS.map((weekday) => (
              <span key={weekday} className="py-1">
                {weekday}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 pt-3">
            {calendarDays.map(({ date, isCurrentMonth }) => {
              if (!isCurrentMonth) {
                return (
                  <div key={date.format('YYYY-MM-DD')} className="m-0.5 w-8" />
                );
              }

              const isSelected =
                !!selectedDate && date.isSame(selectedDate, 'day');
              const isToday = date.isSame(today, 'day');

              return (
                <button
                  key={date.format('YYYY-MM-DD')}
                  type="button"
                  onClick={() => onSelectDate(date)}
                  className={classNames(
                    'm-0.5 size-8 rounded-md text-sm transition hover:bg-navy-50',
                    !isSelected && 'text-gray-700',
                    isSelected && 'bg-navy-700 text-white hover:bg-navy-700',
                    !isSelected && isToday && 'border border-navy-200'
                  )}
                  data-testid={`${dataTestId}-day-${date.format('YYYY-MM-DD')}`}
                >
                  {date.date()}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          className="grid max-h-[220px] grid-cols-3 gap-1 overflow-y-scroll custom-scroll"
          data-testid={`${dataTestId}-years`}
        >
          {calendarYears.map((year) => {
            const isActiveYear = year === viewDate.year();

            return (
              <button
                key={year}
                ref={isActiveYear ? activeYearRef : undefined}
                type="button"
                onClick={() => handleSelectYear(year)}
                className={classNames(
                  'rounded py-1.5 text-sm hover:bg-navy-50',
                  isActiveYear ? 'bg-navy-700 text-white' : 'text-gray-700'
                )}
                data-testid={`${dataTestId}-year-${year}`}
              >
                {year}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DatePickerCalendar;
